import { usePreferences } from '../theme/PreferencesContext';
import { useT } from '../i18n';
import { IconMoon, IconSun } from './icons';

interface ThemeSwitchProps {
  /**
   * 'row' — icon, label and toggle spread across a settings card, as in the
   * mobile app's Settings screen. 'compact' — the same control sized for the
   * app bar's user dropdown.
   */
  variant?: 'row' | 'compact';
  /** Called after the theme changes, e.g. to close the menu it lives in. */
  onChange?: () => void;
}

/**
 * Dark / light mode toggle — the Appearance row from
 * mobileapp/src/screens/settings/SettingsScreen.tsx, where a single Switch
 * flips the whole app and the label names the mode you are currently in.
 *
 * Applies immediately with no Save button: the change is local-first and the
 * account sync is fire-and-forget (see theme/PreferencesContext).
 *
 * `role="switch"` rather than a checkbox — this is a live setting that takes
 * effect on click, not a form field that gets submitted, and assistive tech
 * should announce it as on/off rather than checked/unchecked.
 */
export const ThemeSwitch = ({ variant = 'row', onChange }: ThemeSwitchProps) => {
  const { darkMode, toggleDarkMode } = usePreferences();
  const t = useT();

  const label = darkMode ? t('settings.darkMode') : t('settings.lightMode');

  return (
    <div className={`theme-switch theme-switch--${variant}`}>
      <span className="theme-switch__label">
        {darkMode ? <IconMoon size={16} /> : <IconSun size={16} />}
        <span>{label}</span>
      </span>
      <button
        type="button"
        className="theme-switch__track"
        role="switch"
        aria-checked={darkMode}
        aria-label={t('settings.darkMode')}
        onClick={() => {
          toggleDarkMode();
          onChange?.();
        }}
      >
        <span className="theme-switch__thumb" />
      </button>
    </div>
  );
};
